import React, { useState, useEffect, useRef } from "react";
import SidebarTopbar from "../layouts/layout";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { RadioButtonUnchecked as CircleIcon, CheckCircle as CheckCircleIcon, ArrowBack as ArrowBackIcon, CloudUpload as CloudUploadIcon, Autorenew as AutorenewIcon, Close as CloseIcon } from "@mui/icons-material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import PropTypes from "prop-types";
import PresentationViewDialog from "./PresentationViewDialog";
import { useWebSockets } from "../../contexts/WebSocketContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const PRIMARY_BLUE = "#1E4DE3";

const SlideEditorView = ({
  open,
  onClose,
  pptId,
  token,
  publicationId,
  onPptCreated,
  isAlreadyMerged,
  initialPptUrl,
  professionalType
}) => {
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [slides, setSlides] = useState([]);
  const [slideVersions, setSlideVersions] = useState({});
  const [selectedVersion, setSelectedVersion] = useState({});
  const [regeneratingSlideIds, setRegeneratingSlideIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlides, setSelectedSlides] = useState(new Set());
  const [isMerging, setIsMerging] = useState(false);
  const [mergeMessage, setMergeMessage] = useState("");
  const [mergeComplete, setMergeComplete] = useState(false);
  const [pptDialogOpen, setPptDialogOpen] = useState(false);
  const [pptUrl, setPptUrl] = useState(null);
  const [uploadingSlotKeys, setUploadingSlotKeys] = useState(new Set());
  const [regeneratingSlotKeys, setRegeneratingSlotKeys] = useState(new Set());
  const [mergeProjectId, setMergeProjectId] = useState(null);
  const [mergeProgress, setMergeProgress] = useState(0);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);
  const [regenerationMessages, setRegenerationMessages] = useState({});
  const [regeneratedImageDataMap, setRegeneratedImageDataMap] = useState({});
  const [selectedImageForApplyMap, setSelectedImageForApplyMap] = useState({});
  const [applyingImageIds, setApplyingImageIds] = useState(new Set());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);

  const fileInputRef = useRef(null);
  const { activeTasks, setActiveTasks, taskStatus, setTaskStatus, setupWebSocket } = useWebSockets();

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  // Show alert helper function
  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") return;
    setAlertOpen(false);
  };

  // Toggle slide selection
  const toggleSlideSelection = (index, e) => {
    e.stopPropagation();
    setSelectedSlides(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Select all slides
  const handleSelectAll = () => {
    if (selectedSlides.size === slides.length) {
      // Deselect all if all are selected
      setSelectedSlides(new Set());
    } else {
      // Select all
      setSelectedSlides(new Set(slides.map((_, index) => index)));
    }
  };

  // Merge PPTs with WebSocket integration
  const handleMergePPT = async () => {
    // Require at least 2 slides to create PPT
    if (selectedSlides.size <= 1) {
      console.log("[SlideEditorView] Need at least 2 slides selected to merge");
      showAlert("Please select at least 2 slides to create PPT", "warning");
      return;
    }

    const sequenceIds = slides
      .filter((_, index) => selectedSlides.has(index))
      .map(slide => slide.sequence_id);

    if (sequenceIds.length === 0) {
      console.log("[SlideEditorView] No slides to merge");
      return;
    }

    console.log("[SlideEditorView] Merging sequence IDs:", sequenceIds);
    setIsMerging(true);
    setMergeMessage("Starting...");
    setMergeComplete(false);
    setMergeProgress(0);
    setMergeProjectId(null); // Reset project ID for new merge

    // Don't reset global taskStatus here to avoid wiping other active projects.
    // We'll initialize the specific task_id once we receive it.
    try {
      // Call merge endpoint
      const mergeResponse = await fetch(`${BASE_URL}/merge_ppts_from_sequences`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sequenceIds),
      });

      if (!mergeResponse.ok) {
        showAlert("Error while creating PPT, please try again", "error");
        setMergeMessage("");
        setIsMerging(false);
        return;
      }

      const mergeText = await mergeResponse.text();
      console.log("[SlideEditorView] RAW merge response text:", mergeText);
      let mergeDataRaw;
      try {
        mergeDataRaw = JSON.parse(mergeText);
      } catch (e) {
        throw new Error("Failed to parse JSON response: " + mergeText);
      }
      const mergeData = mergeDataRaw.response || mergeDataRaw;
      console.log("[SlideEditorView] Parsed merge response:", mergeData);

      // Store project_id for later use
      if (mergeData.project_id) {
        setMergeProjectId(mergeData.project_id);
      }

      // Use task_id from response to connect to WebSocket
      if (mergeData.task_id) {
        const uniqueId = mergeData.project_id || mergeData.task_id;
        console.log(`[SlideEditorView] Connecting to WebSocket with task_id: ${mergeData.task_id}, uniqueId: ${uniqueId}`);
        setupWebSocket(mergeData.task_id, "ppt_merge", publicationId, mergeData.project_id, (message, severity) => {
          showAlert(message, severity);
        });
        setActiveTasks(prev => {
          const currentArr = prev.ppt_merge || [];
          if (!currentArr.includes(uniqueId)) {
            return { ...prev, ppt_merge: [...currentArr, uniqueId] };
          }
          return prev;
        });

        setMergeMessage("Processing...");
        // showAlert("PPT creation started successfully", "success");
      } else {
        // If no task_id but has project_id, merge might have completed immediately
        if (mergeData.project_id) {
          console.log("[SlideEditorView] Merge completed immediately with project_id:", mergeData.project_id);
          const pptDownloadUrl = `${BASE_URL}/ppt/${mergeData.project_id}`;
          setPptUrl(pptDownloadUrl);
          setMergeComplete(true);
          setMergeProgress(100);
          setMergeMessage("PPT generated successfully!");
          setIsMerging(false);
          showAlert("PPT created successfully!", "success");

          // Notify parent component that PPT was created
          if (onPptCreated) {
            onPptCreated(pptDownloadUrl, mergeData.project_id);
          }

          const timeoutId = setTimeout(() => {
            if (open) {
              setPptDialogOpen(true);
            }
          }, 10000);
          timeoutRefs.current.push(timeoutId);
        } else {
          throw new Error("No task_id or project_id in response");
        }
      }

    } catch (err) {
      console.error("[SlideEditorView] Error merging PPT:", err);
      showAlert(`Error: ${err.message}`, "error");
      setMergeMessage("");
      setIsMerging(false);
      console.error("[SlideEditorView] Merge error details:", err.message);
    }
  };

  // View PPT - open dialog
  const handleViewPPT = () => {
    if (pptUrl) {
      setPptDialogOpen(true);
    }
  };

  // Set initial state for already merged PPTs
  useEffect(() => {
    if (open) {
      if (isAlreadyMerged) {
        setMergeComplete(true);
        if (initialPptUrl) {
          setPptUrl(initialPptUrl);
        }
        setMergeMessage("PPT generated successfully!");
      } else {
        setMergeComplete(false);
        setPptUrl(null);
        setMergeMessage("");
      }
    }
  }, [open, isAlreadyMerged, initialPptUrl]);

  // Prevent accidental navigation when generating/uploading images
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploadingSlotKeys.size > 0 || regeneratingSlotKeys.size > 0 || applyingImageIds.size > 0) {
        e.preventDefault();
        e.returnValue = "Changes you made may not be saved.";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [uploadingSlotKeys.size, regeneratingSlotKeys.size, applyingImageIds.size]);

  // Fetch slides data when the view opens
  useEffect(() => {
    if (open && pptId && token) {
      fetchSlides();
    }
  }, [open, pptId, token]);

  // Check for in-progress PPT merge when dialog opens
  useEffect(() => {
    if (open && pptId && publicationId) {
      // Check if there's an active ppt_merge task from WebSocket
      const pptMergeTasks = Object.values(taskStatus.ppt_merge || {});
      const status = pptMergeTasks.find(t => {
        if (!t) return false;
        return Number(t.pptId) === Number(pptId) && Number(t.publicationId) === Number(publicationId);
      });

      console.log("[SlideEditorView] Checking for active ppt_merge task:", {
        pptId,
        publicationId,
        activeTasks,
        status,
        taskStatusPpt: taskStatus.ppt,
        taskStatusPptMerge: taskStatus.ppt_merge,
        hasTaskStatus: !!status,
        statusProgress: status?.progress,
        statusPublicationId: status?.publicationId
      });

      // Check if taskStatus has PPT merge data (even if activeTasks not set yet)
      if (status && status.progress > 0) {
        // Check if this task belongs to the current publication AND current project
        const publicationMatches = !status.publicationId || Number(status.publicationId) === Number(publicationId);
        const pptIdMatchesProp = !status.pptId || Number(status.pptId) === Number(pptId);

        // If there's an active merge task with progress < 100 and matches publication & project (not terminal)
        if (status.progress > 0 && status.progress < 100 && status.status !== "completed" && status.status !== "failed" && status.status !== "error" && publicationMatches && pptIdMatchesProp) {
          console.log("[SlideEditorView] Restoring in-progress PPT merge:", {
            progress: status.progress,
            pptId: status.pptId,
            publicationId: status.publicationId,
            status: status.status
          });

          // Restore merge state
          setIsMerging(true);
          setMergeProgress(status.progress);
          setMergeMessage("Processing...");
          setMergeComplete(false);

          // Set project ID if available
          if (status.pptId) {
            setMergeProjectId(status.pptId);
          }
        }
        // Handle case where progress is 100 but completion flow hasn't run yet
        else if (status.progress >= 100 && publicationMatches && pptIdMatchesProp && status.pptId) {
          console.log("[SlideEditorView] Restoring completed PPT merge at 100%:", {
            progress: status.progress,
            pptId: status.pptId,
            publicationId: status.publicationId,
            status: status.status
          });

          // Set states for completed merge
          setMergeProjectId(status.pptId);
          setMergeProgress(100);
          const pptDownloadUrl = `${BASE_URL}/ppt/${status.pptId}`;
          setPptUrl(pptDownloadUrl);
          setMergeComplete(true);
          setMergeMessage("PPT generated successfully!");
          setIsMerging(false);
        }
      }
    }
  }, [open, pptId, publicationId, activeTasks, taskStatus, BASE_URL]);

  // Check for in-progress video tasks when dialog opens
  // Removed video monitoring - should be in VideoEditorView instead

  // Store timeout IDs for cleanup
  const timeoutRefs = useRef([]);

  // Cleanup merge state when dialog closes
  useEffect(() => {
    if (!open) {
      // Clear any pending timeouts
      timeoutRefs.current.forEach(id => clearTimeout(id));
      timeoutRefs.current = [];
      
      // Reset all merge-related state when dialog closes
      setIsMerging(false);
      setMergeComplete(false);
      setMergeProgress(0);
      setMergeMessage("");
      setMergeProjectId(null);
      setPptDialogOpen(false);
      setPptUrl(null);
    }
  }, [open]);

  // Monitor WebSocket status for PPT merge completion
  useEffect(() => {
    // Only care about ppt_merge task types for the create ppt flow
    const pptMergeTasks = Object.values(taskStatus.ppt_merge || {});
    const pptTaskStatus = pptMergeTasks.find(t => {
      if (!t) return false;
      return Number(t.pptId) === Number(pptId) && Number(t.publicationId) === Number(publicationId);
    });

    if (!pptTaskStatus) return;

    // Update progress based on WebSocket - but only for matching publicationId AND pptId
    const publicationMatches = !pptTaskStatus.publicationId || Number(pptTaskStatus.publicationId) === Number(publicationId);
    const pptIdMatchesProp = !pptTaskStatus.pptId || Number(pptTaskStatus.pptId) === Number(pptId);

    if (pptTaskStatus.progress > 0 && pptTaskStatus.status !== "completed" && pptTaskStatus.status !== "failed" && pptTaskStatus.status !== "error" && publicationMatches && pptIdMatchesProp) {
      setMergeProgress(pptTaskStatus.progress);
      if (pptTaskStatus.progress < 100) {
        setMergeMessage(`Processing...`);
        // Ensure isMerging is true when progress is actively running
        if (!isMerging) {
          setIsMerging(true);
        }
        // Restore mergeProjectId if available and not already set
        if (pptTaskStatus.pptId && !mergeProjectId) {
          setMergeProjectId(pptTaskStatus.pptId);
        }
      }
    }

    // Enable View PPT button when progress reaches 100
    // Only trigger completion if we have mergeProjectId (set after API call succeeds)
    // This prevents stale progress from previous tasks triggering completion
    const progress = Number(pptTaskStatus.progress);
    const isCompleted = pptTaskStatus.status === "completed" ||
      pptTaskStatus.status === "success" ||
      pptTaskStatus.status === "finished" ||
      pptTaskStatus.status === "";
    const pptIdMatches = !pptTaskStatus.pptId || Number(pptTaskStatus.pptId) === Number(mergeProjectId);

    console.log("[SlideEditorView] ✓ WebSocket status check:", {
      mergeProjectId,
      mergeComplete,
      progress,
      rawProgress: pptTaskStatus.progress,
      pptIdFromSocket: pptTaskStatus.pptId,
      publicationIdFromSocket: pptTaskStatus.publicationId,
      currentPublicationId: publicationId,
      status: pptTaskStatus.status,
      isCompleted,
      pptIdMatches,
      publicationMatches,
      taskType: "ppt_merge",
      allConditionsMet: mergeProjectId && progress >= 100 && pptIdMatches && publicationMatches && isCompleted && !mergeComplete
    });

    if (
      mergeProjectId &&
      progress >= 100 &&
      pptIdMatches &&
      publicationMatches &&
      isCompleted &&
      !mergeComplete
    ) {
      console.log("[SlideEditorView] ✓✓ PPT merge completed - Triggering completion flow");

      // IMMEDIATELY set states to enable View PPT button
      // Don't wait for async fetch to complete
      const pptDownloadUrl = `${BASE_URL}/ppt/${mergeProjectId}`;
      setPptUrl(pptDownloadUrl);
      setMergeComplete(true);
      setMergeProgress(100);
      setMergeMessage("PPT generated successfully!");
      setIsMerging(false);
      showAlert("PPT created successfully!", "success");

      // Notify parent immediately with fallback URL
      if (onPptCreated) {
        onPptCreated(pptDownloadUrl, mergeProjectId);
      }

      console.log("[SlideEditorView] ✓✓ States updated - mergeComplete set to TRUE, pptUrl:", pptDownloadUrl);

      // Then fetch the actual PPT URL in background
      const fetchPptUrl = async () => {
        try {
          const projectId = mergeProjectId;
          if (!projectId) {
            console.error("[SlideEditorView] No project_id available");
            return;
          }

          const response = await fetch(`${BASE_URL}/ppt_project_current_version?pptProjectId=${projectId}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            console.log("[SlideEditorView] PPT current version response:", data);

            // Update the PPT URL if we got a better one from the API
            if (data.url || data.pptUrl) {
              const betterPptUrl = data.url || data.pptUrl;
              console.log("[SlideEditorView] Updating PPT URL to:", betterPptUrl);
              setPptUrl(betterPptUrl);

              // Notify parent component with the better URL
              if (onPptCreated) {
                onPptCreated(betterPptUrl, projectId);
              }
            }

            // NOTE: fetchNotifications is called by WebSocketContext global handler — no duplicate needed here

            const timeoutId = setTimeout(() => {
              if (open) {
                setPptDialogOpen(true);
              }
            }, 10000);
            timeoutRefs.current.push(timeoutId);
          } else {
            console.log("[SlideEditorView] Failed to fetch better PPT URL, using fallback");
          }
        } catch (err) {
          console.error("[SlideEditorView] Error fetching PPT URL:", err);
        }
      };

      fetchPptUrl();
    }

    // Handle error status - only for current merge and publication
    const isError = pptTaskStatus.status === "error" || pptTaskStatus.status === "failed";
    const errorIdMatches = !pptTaskStatus.pptId || Number(pptTaskStatus.pptId) === Number(mergeProjectId);
    const errorPublicationMatches = !pptTaskStatus.publicationId || Number(pptTaskStatus.publicationId) === Number(publicationId);

    if (isError && mergeProjectId && errorIdMatches && errorPublicationMatches && !mergeComplete) {
      console.error("[SlideEditorView] PPT merge failed via WebSocket");
      showAlert("Error while creating PPT, please try again", "error");
      setMergeMessage("");
      setMergeProgress(0);
      setIsMerging(false);
      setMergeComplete(false);
    }

    // Fallback: If progress is 100 for more than 3 seconds and not yet complete,
    // try to complete the process anyway (in case conditions above didn't trigger)
    if (mergeProjectId && progress >= 100 && publicationMatches && !mergeComplete) {
      console.log("[SlideEditorView] ⚠ Fallback timer started - Progress 100% but not yet complete");
      const fallbackTimer = setTimeout(() => {
        console.log("[SlideEditorView] ⚡ FALLBACK TRIGGERED: Forcing completion after 100% progress");
        if (mergeProjectId && !mergeComplete) {
          // Construct PPT URL and complete
          const pptDownloadUrl = `${BASE_URL}/ppt/${mergeProjectId}`;
          setPptUrl(pptDownloadUrl);
          setMergeComplete(true);
          setMergeProgress(100);
          setMergeMessage("PPT generated successfully!");
          setIsMerging(false);
          showAlert("PPT created successfully!", "success");

          if (onPptCreated) {
            onPptCreated(pptDownloadUrl, mergeProjectId);
          }

          // NOTE: fetchNotifications is called by WebSocketContext global handler — no duplicate needed here

          console.log("[SlideEditorView] ⚡ Fallback completion done - mergeComplete set to TRUE");
        }
      }, 3000); // Reduced from 5s to 3s

      return () => clearTimeout(fallbackTimer);
    }
  }, [taskStatus.ppt, taskStatus.ppt_merge, activeTasks.ppt, activeTasks.ppt_merge, mergeProjectId, mergeComplete, isMerging, BASE_URL, token, publicationId, onPptCreated]);

  // When slides change, initialize version state
  useEffect(() => {
    if (slides.length > 0) {
      const newVersions = {};
      const newSelected = {};
      slides.forEach(slide => {
        const total = slide.content_total_versions || 1;
        newVersions[slide.sequence_id] = Array.from({ length: total }, (_, i) => i + 1);
        newSelected[slide.sequence_id] = slide.content_version_number || total;
      });
      setSlideVersions(newVersions);
      setSelectedVersion(newSelected);
    }
  }, [slides]);

  // Reset selected slot when switching slides
  useEffect(() => {
    setSelectedSlotIndex(null);
  }, [selectedSlideIndex]);

  const fetchSlides = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log("[SlideEditorView] Fetching content for pptId:", pptId);
      const response = await fetch(
        `${BASE_URL}/process_template_content?pptId=${pptId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch slide content");
      }

      const data = await response.json();
      console.log("[SlideEditorView] Received data:", data);

      // Handle nested response structure
      const responseData = data.response || data;
      console.log("[SlideEditorView] Response data:", responseData);
      console.log("[SlideEditorView] Results:", responseData.results);
      console.log("[SlideEditorView] Results length:", responseData.results?.length);

      const results = responseData.results || [];
      setSlides(results);
      if (results.length > 0) {
        setSelectedSlides(new Set(results.map((_, index) => index)));
      }
    } catch (err) {
      console.error("[SlideEditorView] Error fetching slides:", err);
      setError("Failed to load slide content. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const selectedSlide = slides[selectedSlideIndex] || null;

  // Get all values from llm_response
  const getSlideContent = (slide) => {
    if (!slide?.llm_response) return [];
    return slide.llm_response;
  };

  // Regenerate slide content
  const handleRegenerate = async () => {
    const slide = slides[selectedSlideIndex];
    if (!slide) return;
    setRegeneratingSlideIds(prev => new Set([...prev, slide.sequence_id]));
    showAlert("Regeneration started", "info");
    try {
      const response = await fetch(`${BASE_URL}/ppt_regenerate?sequenceId=${slide.sequence_id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to regenerate slide");
      const data = await response.json();
      const newSlide = data.response;
      // Update slides array immutably mapping by ID to avoid stale closure / index mismatches
      setSlides(prev => prev.map((s) => s.sequence_id === slide.sequence_id ? { ...s, ...newSlide } : s));
      // Update versions and selected version
      setSlideVersions(prev => ({
        ...prev,
        [newSlide.sequence_id]: Array.from({ length: newSlide.content_total_versions || 1 }, (_, i) => i + 1)
      }));
      setSelectedVersion(prev => ({
        ...prev,
        [newSlide.sequence_id]: newSlide.content_version_number || 1
      }));
      showAlert("Slide regenerated successfully", "success");
    } catch (err) {
      console.error("[SlideEditorView] Error regenerating slide:", err);
      showAlert("Failed to regenerate slide", "error");
    } finally {
      setRegeneratingSlideIds(prev => {
        const next = new Set(prev);
        next.delete(slide.sequence_id);
        return next;
      });
    }
  };

  // Change version - rollback to selected version
  const handleVersionChange = async (e) => {
    const version = Number(e.target.value);
    const slide = slides[selectedSlideIndex];
    if (!slide) return;
    setRegeneratingSlideIds(prev => new Set([...prev, slide.sequence_id]));
    try {
      // Call rollback endpoint to switch to selected version
      const response = await fetch(`${BASE_URL}/ppt_rollback_version?sequenceId=${slide.sequence_id}&versionNumber=${version}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to rollback to selected version");
      const data = await response.json();
      console.log("[SlideEditorView] Rollback response:", data);

      // Update the slide with the rolled back content
      setSlides(prev => prev.map((s) => {
        if (s.sequence_id === slide.sequence_id) {
          return {
            ...s,
            content_selected_version: data.content_selected_version,
            content_total_versions: data.content_total_versions,
            content_version_number: data.content_selected_version,
            llm_response: data.llm_response
          };
        }
        return s;
      }));

      // Update versions array if total versions changed
      setSlideVersions(prev => ({
        ...prev,
        [slide.sequence_id]: Array.from({ length: data.content_total_versions || 1 }, (_, i) => i + 1)
      }));

      // Update selected version
      setSelectedVersion(prev => ({
        ...prev,
        [slide.sequence_id]: data.content_selected_version
      }));
      showAlert("Version restored successfully", "success");
    } catch (err) {
      console.error("[SlideEditorView] Error rolling back version:", err);
      showAlert("Failed to restore version", "error");
    } finally {
      setRegeneratingSlideIds(prev => {
        const next = new Set(prev);
        next.delete(slide.sequence_id);
        return next;
      });
    }
  };

  // Get AI image slots (imageType === "Ai")
  const getAiSlots = (slide) => {
    if (!slide?.images?.slots) return [];
    return slide.images.slots.filter(slot => slot.imageType === "Ai");
  };

  // Get non-AI image slots (imageType === "nonAi")
  const getNonAiSlots = (slide) => {
    if (!slide?.images?.slots) return [];
    return slide.images.slots.filter(slot => slot.imageType === "nonAi");
  };

  // Get icon slots (imageType === "icon")
  const getIconSlots = (slide) => {
    if (!slide?.images?.slots) return [];
    return slide.images.slots.filter(slot => slot.imageType === "icon");
  };

  // Get all non-icon slots (AI + nonAi)
  const getAllImageSlots = (slide) => {
    if (!slide?.images?.slots) return [];
    return slide.images.slots.filter(slot => slot.imageType !== "icon");
  };

  // Check if slide has images
  const hasImages = (slide) => {
    return getAllImageSlots(slide).length > 0;
  };

  // Get slot by index
  const getSlotByIndex = (slide, slotIndex) => {
    if (!slide?.images?.slots) return null;
    return slide.images.slots.find(slot => slot.slot_index === slotIndex);
  };

  // Get currently selected slot for operations
  const getSelectedSlot = (slide) => {
    if (selectedSlotIndex === null) {
      // Default to first non-icon slot
      const slots = getAllImageSlots(slide);
      return slots.length > 0 ? slots[0] : null;
    }
    return getSlotByIndex(slide, selectedSlotIndex);
  };

  // Get all versions for a specific slot
  const getSlotVersions = (slot) => {
    if (!slot) return [];

    // Map versions from array to consistent format
    const versions = (slot.versions || []).map(v => ({
      version: v.version_number,
      url: v.image_url,
      imageType: v.imageType,
      created_at: v.created_at,
      is_current: v.version_number === slot.selected_version
    }));

    // Always add current version if we have current_url and it's not already in the list
    if (slot.current_url && slot.selected_version) {
      const currentExists = versions.some(v => v.version === slot.selected_version);
      if (!currentExists) {
        versions.push({
          version: slot.selected_version,
          url: slot.current_url,
          imageType: slot.imageType,
          is_current: true
        });
      }
    }

    return versions;
  };

  // Helper to read SSE stream
  const readSSEStream = async (response, slotKey, targetSlotIndex, setKeysStateFn) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep partial line in buffer

        for (const line of lines) {
          if (line.trim() === '') {
            currentEvent = null;
          } else if (line.startsWith("event: ")) {
            currentEvent = line.substring(7).trim();
          } else if (line.startsWith("data: ")) {
            const dataStr = line.substring(6).trim();
            if (dataStr === "keep-alive") continue;

            try {
              const data = JSON.parse(dataStr);
              if (currentEvent === "status") {
                setRegenerationMessages(prev => ({ ...prev, [slotKey]: data.message }));
              } else if (currentEvent === "error") {
                throw new Error(data.detail || "Error processing image");
              } else if (currentEvent === "result") {
                if (data.status === "success" && data.slots) {
                  setSlides(prev => prev.map((s) => {
                    if (s.sequence_id === data.sequenceId) {
                      return {
                        ...s,
                        images: {
                          ...(s.images || {}),
                          slots: data.slots,
                          image_count: data.image_count,
                        }
                      };
                    }
                    return s;
                  }));
                  showAlert(data.message || "Image processed successfully", "success");
                }
              }
            } catch (err) {
              if (err.message && err.message !== "Unexpected end of JSON input" && !err.message.includes("JSON")) {
                throw err;
              }
            }
          }
        }
      }
    } finally {
      setRegenerationMessages(prev => {
        const next = { ...prev };
        delete next[slotKey];
        return next;
      });
      setKeysStateFn(prev => {
        const next = new Set(prev);
        next.delete(slotKey);
        return next;
      });
    }
  };

  // Handle image upload for a specific slot
  const handleImageUpload = async (event, slotIndex = null) => {
    const file = event.target.files?.[0];
    if (!file || !selectedSlide) return;

    const targetSlotIndex = slotIndex !== null ? slotIndex : selectedSlotIndex;
    const slotKey = `${selectedSlide.sequence_id}_${targetSlotIndex}`;
    
    setUploadingSlotKeys(prev => new Set([...prev, slotKey]));
    try {
      const formData = new FormData();
      formData.append("imageFile", file);
      formData.append("regenerate", "false");

      let queryParams = `sequenceId=${selectedSlide.sequence_id}`;
      if (targetSlotIndex !== null) {
        queryParams += `&slotIndex=${targetSlotIndex}`;
      }

      const response = await fetch(
        `${BASE_URL}/ppt_image_regenerate?${queryParams}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Failed to upload image");
      
      await readSSEStream(response, slotKey, targetSlotIndex, setUploadingSlotKeys);
      
    } catch (err) {
      console.error("[SlideEditorView] Error uploading image:", err);
      showAlert(err.message || "Failed to upload image", "error");
      setUploadingSlotKeys(prev => {
        const next = new Set(prev);
        next.delete(slotKey);
        return next;
      });
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handle image regenerate (AI only) for a specific slot
  const handleImageRegenerate = async (slotIndex = null) => {
    if (!selectedSlide) return;

    const targetSlotIndex = slotIndex !== null ? slotIndex : selectedSlotIndex;
    const slotKey = `${selectedSlide.sequence_id}_${targetSlotIndex}`;

    setRegeneratingSlotKeys(prev => new Set([...prev, slotKey]));
    try {
      const formData = new FormData();
      formData.append("regenerate", "true");

      let queryParams = `sequenceId=${selectedSlide.sequence_id}`;
      if (targetSlotIndex !== null) {
        queryParams += `&slotIndex=${targetSlotIndex}`;
      }

      const response = await fetch(
        `${BASE_URL}/ppt_image_regenerate?${queryParams}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Failed to regenerate image");
      
      await readSSEStream(response, slotKey, targetSlotIndex, setRegeneratingSlotKeys);
      
    } catch (err) {
      console.error("[SlideEditorView] Error regenerating image:", err);
      showAlert(err.message || "Failed to regenerate image", "error");
      setRegeneratingSlotKeys(prev => {
        const next = new Set(prev);
        next.delete(slotKey);
        return next;
      });
    }
  };

  // Handle image version selection for a specific slot
  const handleSelectImageVersion = async (slotIndex, version) => {
    if (!selectedSlide) return;

    setApplyingImageIds(prev => new Set([...prev, selectedSlide.sequence_id]));
    try {
      // Build selections object: slot_index -> version_number
      const selections = {
        [slotIndex.toString()]: version
      };

      const response = await fetch(
        `${BASE_URL}/ppt_select_image?sequence_id=${selectedSlide.sequence_id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(selections),
        }
      );

      if (!response.ok) throw new Error("Failed to select image version");
      const data = await response.json();
      console.log("[SlideEditorView] Select image response:", data);

      // Update the slide with selected version - slots-based response
      if (data.status === "success" && data.slots) {
        const updatedSlide = {
          ...selectedSlide,
          images: {
            ...selectedSlide.images,
            slots: data.slots,
          },
        };
        setSlides(prev => prev.map((s, idx) => idx === selectedSlideIndex ? updatedSlide : s));
        showAlert(data.message || "Image version applied successfully", "success");
      }
    } catch (err) {
      console.error("[SlideEditorView] Error applying image version:", err);
      showAlert("Failed to apply image version", "error");
    } finally {
      setApplyingImageIds(prev => {
        const next = new Set(prev);
        next.delete(selectedSlide.sequence_id);
        return next;
      });
    }
  };

  // If not open, don't render
  if (!open) {
    return null;
  }

  return (
    <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1300, backgroundColor: "#fff", fontFamily: "'Inter', sans-serif" }}>
      <SidebarTopbar>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            backgroundColor: "#fff",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 24px",
              backgroundColor: "#fff",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={onClose}
                sx={{ color: PRIMARY_BLUE }}
                title="Back to Assets"
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" fontWeight="600">
                SLIDES({slides.length})
              </Typography>
              {slides.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSelectAll}
                  sx={{
                    borderColor: PRIMARY_BLUE,
                    color: PRIMARY_BLUE,
                    "&:hover": { borderColor: "#1748b8", backgroundColor: "#f5f7ff" },
                  }}
                >
                  {selectedSlides.size === slides.length ? "Deselect All" : "Select All"}
                </Button>
              )}
              {professionalType && (
                <Chip
                  label={`Audience: ${professionalType}`}
                  variant="outlined"
                  sx={{
                    borderColor: PRIMARY_BLUE,
                    color: PRIMARY_BLUE,
                    fontWeight: 600,
                    fontSize: "13px",
                    height: "32px",
                    borderRadius: "8px",
                    backgroundColor: "#e8f0fe",
                    px: 1,
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  }}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Tooltip
                title={selectedSlides.size <= 1 ? "Please select at least 2 slides to create PPT" : "Select slides you want to create"}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleMergePPT}
                    disabled={isMerging || selectedSlides.size <= 1}
                    sx={{
                      backgroundColor: PRIMARY_BLUE,
                      "&:hover": { backgroundColor: "#1748b8" },
                      "&:disabled": {
                        backgroundColor: isMerging ? PRIMARY_BLUE : "#ccc",
                        color: isMerging ? "#fff" : "#666",
                      },
                      minWidth: "150px",
                      textTransform: "none",
                    }}
                  >
                    {isMerging ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ position: "relative", display: "inline-flex" }}>
                          <CircularProgress
                            variant="determinate"
                            value={mergeProgress}
                            size={20}
                            sx={{ color: "#fff" }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography variant="caption" sx={{ color: "#fff", fontSize: "8px", fontWeight: "bold" }}>
                              {mergeProgress}
                            </Typography>
                          </Box>
                        </Box>
                        <span>Creating...</span>
                      </Box>
                    ) : (
                      `Create PPT${selectedSlides.size > 0 ? ` (${selectedSlides.size})` : ""}`
                    )}
                  </Button>
                </span>
              </Tooltip>

              {mergeComplete && (
                <Button
                  variant="contained"
                  onClick={handleViewPPT}
                  sx={{
                    backgroundColor: PRIMARY_BLUE,
                    "&:hover": { backgroundColor: "#1748b8" },
                    textTransform: "none",
                  }}
                >
                  View PPT
                </Button>
              )}
            </Box>
          </Box>

          {/* Content */}
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: PRIMARY_BLUE }} />
              <Typography variant="h6">Loading Slide Content...</Typography>
            </Box>
          ) : error ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
              }}
            >
              <Typography variant="h6" color="error">{error}</Typography>
              <Button
                variant="contained"
                onClick={fetchSlides}
                sx={{
                  backgroundColor: PRIMARY_BLUE,
                  "&:hover": { backgroundColor: "#1748b8" },
                }}
              >
                Retry
              </Button>
            </Box>
          ) : slides.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
              }}
            >
              <Typography variant="h6">No slides available</Typography>
              <Typography variant="body2" color="textSecondary">
                Generate a presentation first to view the content.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
              {/* Left Sidebar - Slide Outline */}
              <Box
                sx={{
                  width: "280px",
                  backgroundColor: "#fff",
                  borderRight: "1px solid #e0e0e0",
                  overflowY: "auto",
                  padding: "16px 0",
                }}
              >
                {slides.map((slide, index) => {
                  // Get the first llm_response value as subtitle
                  const slideTitle = slide.llm_response?.[0]?.values?.[0];
                  const displayTitle = slideTitle ? slideTitle.substring(0, 25) : "";

                  return (
                    <Box
                      key={slide.sequence_id || index}
                      onClick={() => setSelectedSlideIndex(index)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "12px 16px",
                        cursor: "pointer",
                        backgroundColor: selectedSlideIndex === index ? "#e8eef9" : "transparent",
                        borderLeft: selectedSlideIndex === index ? `3px solid ${PRIMARY_BLUE}` : "3px solid transparent",
                        "&:hover": {
                          backgroundColor: selectedSlideIndex === index ? "#e8eef9" : "#f5f5f5",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          color: PRIMARY_BLUE,
                          fontWeight: 600,
                          minWidth: "24px",
                        }}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: selectedSlideIndex === index ? 600 : 500,
                            fontSize: "14px",
                            color: "#333",
                          }}
                        >
                          {slide.template_name || `Slide ${index + 1}`}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#666",
                            marginTop: "4px",
                          }}
                        >
                          {displayTitle}...
                        </Typography>
                      </Box>
                      <Box
                        onClick={(e) => toggleSlideSelection(index, e)}
                        sx={{ cursor: "pointer", display: "flex", alignItems: "center", marginLeft: "auto" }}
                      >
                        {selectedSlides.has(index) ? (
                          <CheckCircleIcon sx={{ fontSize: 18, color: PRIMARY_BLUE }} />
                        ) : (
                          <CircleIcon sx={{ fontSize: 18, color: "#ccc" }} />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Middle - Slide Content */}
              <Box
                sx={{
                  flex: 1,
                  padding: "0px 24px 24px 24px",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                }}
              >
                {selectedSlide && (
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      padding: "24px",
                    }}
                  >
                    {/* Slide Title Heading */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#333",
                          textTransform: "uppercase",
                          fontWeight: 600
                        }}
                      >
                        SLIDE TITLE
                      </Typography>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {selectedSlide?.template_name !== "References" && selectedSlide?.template_name !== "Publication Title" && (
                          <Tooltip title="Credits required: 0.1" arrow>
                            <span>
                              <Button
                                size="small"
                                startIcon={regeneratingSlideIds.has(selectedSlide.sequence_id) ? <CircularProgress size={14} /> : <AutorenewIcon sx={{ fontSize: 16 }} />}
                                onClick={handleRegenerate}
                                disabled={regeneratingSlideIds.has(selectedSlide.sequence_id)}
                                sx={{
                                  backgroundColor: "#f0f4ff",
                                  color: PRIMARY_BLUE,
                                  textTransform: "none",
                                  fontSize: "0.8rem",
                                  "&:hover": { backgroundColor: "#e0e8ff" },
                                }}
                              >
                                {regeneratingSlideIds.has(selectedSlide.sequence_id) ? "Regenerating..." : "Regenerate"}
                              </Button>
                            </span>
                          </Tooltip>
                        )}

                        {selectedSlide && slideVersions[selectedSlide.sequence_id]?.length > 1 && (
                          <Select
                            size="small"
                            value={selectedVersion[selectedSlide.sequence_id] || 1}
                            onChange={handleVersionChange}
                            sx={{
                              minWidth: 60,
                              fontWeight: 700,
                              borderColor: PRIMARY_BLUE,
                              "& .MuiSelect-select": { py: 0.5, px: 1, fontSize: "0.8rem" }
                            }}
                          >
                            {slideVersions[selectedSlide.sequence_id].map((v) => (
                              <MenuItem
                                key={v}
                                value={v}
                                sx={{
                                  fontWeight: (selectedVersion[selectedSlide.sequence_id] || 1) === v ? 700 : 400,
                                  color: (selectedVersion[selectedSlide.sequence_id] || 1) === v ? PRIMARY_BLUE : undefined,
                                }}
                              >
                                {`v${v}`}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ marginBottom: 3 }}>
                      <Typography variant="h6" fontWeight="520" sx={{ color: "#666" }}>
                        {selectedSlide.template_name}
                      </Typography>
                    </Box>

                    {/* Slide Content Heading */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#333",
                        textTransform: "uppercase",
                        fontWeight: 600,
                        marginBottom: 2,
                        marginTop: 3,
                        fontFamily: "'Inter', sans-serif"
                      }}
                    >
                      SLIDE CONTENT
                    </Typography>

                    {getSlideContent(selectedSlide).map((content) => (
                      <Box key={content.name} sx={{ marginBottom: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: PRIMARY_BLUE,
                            textTransform: "uppercase",
                            marginBottom: 1,
                            fontWeight: 600,
                            fontFamily: "'Inter', sans-serif"
                          }}
                        >
                          {content.name?.replaceAll("_", " ")}
                        </Typography>
                        {content.values?.map((value, vIdx) => (
                          <Typography
                            key={`${content.name}-${vIdx}`}
                            sx={{
                              fontSize: "15px",
                              lineHeight: 1.6,
                              color: "#333",
                              marginBottom: 1,
                              whiteSpace: "pre-wrap",
                              fontFamily: "'Inter', sans-serif"
                            }}
                          >
                            {value}
                          </Typography>
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Right - Images Section - Only show if slide has images */}
              {selectedSlide && hasImages(selectedSlide) && (
                <Box
                  sx={{
                    width: "420px",
                    backgroundColor: "#fff",
                    borderLeft: "1px solid #e0e0e0",
                    padding: "24px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    overflowY: "auto",
                  }}
                >
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleImageUpload(e, selectedSlotIndex)}
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    style={{ display: "none" }}
                  />

                  {/* AI Images Section */}
                  {getAiSlots(selectedSlide).length > 0 && (
                    <Box sx={{ marginBottom: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#333",
                          fontWeight: 600,
                          marginBottom: 1.5
                        }}
                      >
                        AI Generated Images
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        {getAiSlots(selectedSlide).map((slot, idx) => {
                          const isSelected = selectedSlotIndex === slot.slot_index;
                          const displayName = slot.layerName || `Image ${idx + 1}`;
                          const slotVersions = getSlotVersions(slot);
                          const showVersionHistory = slotVersions.length > 1 || (slot.total_versions && slot.total_versions > 1);

                          return (
                            <Box key={slot.slot_index} sx={{ width: "100%", marginBottom: 2 }}>
                              {/* Main image card */}
                              <Tooltip
                                title={isSelected ? "Click to deselect" : "Click to select for editing"}
                                arrow
                              >
                                <Box
                                  onClick={() => setSelectedSlotIndex(isSelected ? null : slot.slot_index)}
                                  sx={{
                                    width: "110px",
                                    borderRadius: "8px",
                                    border: isSelected ? `2px solid ${PRIMARY_BLUE}` : "2px solid #e0e0e0",
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    position: "relative",
                                    transition: "all 0.2s",
                                    "&:hover": {
                                      borderColor: PRIMARY_BLUE,
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    },
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={slot.current_url}
                                    alt={displayName}
                                    sx={{
                                      width: "100%",
                                      height: "80px",
                                      objectFit: "cover",
                                    }}
                                  />
                                  {(uploadingSlotKeys.has(`${selectedSlide.sequence_id}_${slot.slot_index}`) || 
                                    regeneratingSlotKeys.has(`${selectedSlide.sequence_id}_${slot.slot_index}`) ||
                                    applyingImageIds.has(selectedSlide.sequence_id)) && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: "80px",
                                        backgroundColor: "rgba(0,0,0,0.7)",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        padding: "4px",
                                        textAlign: "center"
                                      }}
                                    >
                                      <CircularProgress size={20} sx={{ color: "#fff", mb: 0.5 }} />
                                      <Typography sx={{ fontSize: "9px", lineHeight: 1.1 }}>
                                        {regenerationMessages[`${selectedSlide.sequence_id}_${slot.slot_index}`] || 
                                         (applyingImageIds.has(selectedSlide.sequence_id) ? "Applying..." : "Processing...")}
                                      </Typography>
                                    </Box>
                                  )}
                                  <Box sx={{
                                    padding: "6px",
                                    backgroundColor: isSelected ? PRIMARY_BLUE : "#f5f5f5",
                                    color: isSelected ? "#fff" : "#666",
                                  }}>
                                    <Typography sx={{ fontSize: "10px", fontWeight: 500, textAlign: "center" }}>
                                      {displayName}
                                    </Typography>
                                    <Typography sx={{ fontSize: "9px", textAlign: "center", opacity: 0.8 }}>
                                      v{slot.selected_version} of {slot.total_versions}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Tooltip>

                              {/* Version history below the image */}
                              {showVersionHistory && (
                                <Box sx={{ marginTop: 1 }}>
                                  <Typography variant="caption" sx={{ color: "#666", fontWeight: 600, display: "block", marginBottom: 0.5 }}>
                                    Versions
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                    {slotVersions
                                      .slice()
                                      .sort((a, b) => (b.version || 0) - (a.version || 0))
                                      .map((imgVersion, vIdx) => {
                                        const isCurrentVersion = imgVersion.version === slot.selected_version;
                                        return (
                                          <Box
                                            key={imgVersion.version || vIdx}
                                            onClick={() => !isCurrentVersion && handleSelectImageVersion(slot.slot_index, imgVersion.version)}
                                            sx={{
                                              width: "55px",
                                              borderRadius: "4px",
                                              border: isCurrentVersion ? `2px solid ${PRIMARY_BLUE}` : "2px solid #e0e0e0",
                                              cursor: isCurrentVersion ? "default" : "pointer",
                                              overflow: "hidden",
                                              opacity: isCurrentVersion ? 1 : 0.7,
                                              "&:hover": {
                                                opacity: 1,
                                                borderColor: PRIMARY_BLUE,
                                              },
                                            }}
                                          >
                                            <Box
                                              component="img"
                                              src={imgVersion.url}
                                              alt={`Version ${imgVersion.version}`}
                                              sx={{
                                                width: "100%",
                                                height: "40px",
                                                objectFit: "cover",
                                              }}
                                            />
                                            <Box
                                              sx={{
                                                backgroundColor: isCurrentVersion ? PRIMARY_BLUE : "rgba(0,0,0,0.6)",
                                                color: "#fff",
                                                fontSize: "8px",
                                                textAlign: "center",
                                                padding: "2px",
                                              }}
                                            >
                                              v{imgVersion.version}
                                              {isCurrentVersion && (
                                                <Box component="span" sx={{ display: "block", color: "#90EE90", fontSize: "7px" }}>
                                                  Applied
                                                </Box>
                                              )}
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Extracted Images Section (non-AI) */}
                  {getNonAiSlots(selectedSlide).length > 0 && (
                    <Box sx={{ marginBottom: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#333",
                          fontWeight: 600,
                          marginBottom: 1.5
                        }}
                      >
                        Extracted Images
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        {getNonAiSlots(selectedSlide).map((slot, idx) => {
                          const isSelected = selectedSlotIndex === slot.slot_index;
                          const displayName = slot.layerName || `Image ${idx + 1}`;
                          const slotVersions = getSlotVersions(slot);
                          const showVersionHistory = slotVersions.length > 1 || (slot.total_versions && slot.total_versions > 1);

                          return (
                            <Box key={slot.slot_index} sx={{ width: "100%", marginBottom: 2 }}>
                              {/* Main image card */}
                              <Tooltip
                                title={isSelected ? "Click to deselect" : "Click to select for editing"}
                                arrow
                              >
                                <Box
                                  onClick={() => setSelectedSlotIndex(isSelected ? null : slot.slot_index)}
                                  sx={{
                                    width: "110px",
                                    borderRadius: "8px",
                                    border: isSelected ? `2px solid ${PRIMARY_BLUE}` : "2px solid #e0e0e0",
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    position: "relative",
                                    transition: "all 0.2s",
                                    "&:hover": {
                                      borderColor: PRIMARY_BLUE,
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    },
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={slot.current_url}
                                    alt={displayName}
                                    sx={{
                                      width: "100%",
                                      height: "80px",
                                      objectFit: "cover",
                                    }}
                                  />
                                  {(uploadingSlotKeys.has(`${selectedSlide.sequence_id}_${slot.slot_index}`) || 
                                    regeneratingSlotKeys.has(`${selectedSlide.sequence_id}_${slot.slot_index}`) ||
                                    applyingImageIds.has(selectedSlide.sequence_id)) && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: "80px",
                                        backgroundColor: "rgba(0,0,0,0.7)",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        padding: "4px",
                                        textAlign: "center"
                                      }}
                                    >
                                      <CircularProgress size={20} sx={{ color: "#fff", mb: 0.5 }} />
                                      <Typography sx={{ fontSize: "9px", lineHeight: 1.1 }}>
                                        {regenerationMessages[`${selectedSlide.sequence_id}_${slot.slot_index}`] || 
                                         (applyingImageIds.has(selectedSlide.sequence_id) ? "Applying..." : "Processing...")}
                                      </Typography>
                                    </Box>
                                  )}
                                  <Box sx={{
                                    padding: "6px",
                                    backgroundColor: isSelected ? PRIMARY_BLUE : "#f5f5f5",
                                    color: isSelected ? "#fff" : "#666",
                                  }}>
                                    <Typography sx={{ fontSize: "10px", fontWeight: 500, textAlign: "center" }}>
                                      {displayName}
                                    </Typography>
                                    <Typography sx={{ fontSize: "9px", textAlign: "center", opacity: 0.8 }}>
                                      v{slot.selected_version} of {slot.total_versions}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Tooltip>

                              {/* Version history below the image */}
                              {showVersionHistory && (
                                <Box sx={{ marginTop: 1 }}>
                                  <Typography variant="caption" sx={{ color: "#666", fontWeight: 600, display: "block", marginBottom: 0.5 }}>
                                    Versions
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                    {slotVersions
                                      .slice()
                                      .sort((a, b) => (b.version || 0) - (a.version || 0))
                                      .map((imgVersion, vIdx) => {
                                        const isCurrentVersion = imgVersion.version === slot.selected_version;
                                        return (
                                          <Box
                                            key={imgVersion.version || vIdx}
                                            onClick={() => !isCurrentVersion && handleSelectImageVersion(slot.slot_index, imgVersion.version)}
                                            sx={{
                                              width: "55px",
                                              borderRadius: "4px",
                                              border: isCurrentVersion ? `2px solid ${PRIMARY_BLUE}` : "2px solid #e0e0e0",
                                              cursor: isCurrentVersion ? "default" : "pointer",
                                              overflow: "hidden",
                                              opacity: isCurrentVersion ? 1 : 0.7,
                                              "&:hover": {
                                                opacity: 1,
                                                borderColor: PRIMARY_BLUE,
                                              },
                                            }}
                                          >
                                            <Box
                                              component="img"
                                              src={imgVersion.url}
                                              alt={`Version ${imgVersion.version}`}
                                              sx={{
                                                width: "100%",
                                                height: "40px",
                                                objectFit: "cover",
                                              }}
                                            />
                                            <Box
                                              sx={{
                                                backgroundColor: isCurrentVersion ? PRIMARY_BLUE : "rgba(0,0,0,0.6)",
                                                color: "#fff",
                                                fontSize: "8px",
                                                textAlign: "center",
                                                padding: "2px",
                                              }}
                                            >
                                              v{imgVersion.version}
                                              {isCurrentVersion && (
                                                <Box component="span" sx={{ display: "block", color: "#90EE90", fontSize: "7px" }}>
                                                  Applied
                                                </Box>
                                              )}
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Selected Slot Details & Actions */}
                  {selectedSlotIndex !== null && getSlotByIndex(selectedSlide, selectedSlotIndex) && (
                    <Box sx={{
                      borderTop: "1px solid #e0e0e0",
                      paddingTop: 2,
                      marginTop: 1
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ color: "#333", fontWeight: 600 }}>
                          Selected
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setSelectedSlotIndex(null)}
                          sx={{
                            color: "#666",
                            "&:hover": { color: "#333", backgroundColor: "#f0f0f0" }
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Main selected image preview */}
                      <Box sx={{ marginBottom: 2 }}>
                        <Box
                          component="img"
                          src={getSlotByIndex(selectedSlide, selectedSlotIndex)?.current_url}
                          alt="Selected Image"
                          sx={{
                            width: "100%",
                            maxHeight: "200px",
                            objectFit: "contain",
                            borderRadius: "8px",
                            border: `2px solid ${PRIMARY_BLUE}`,
                            backgroundColor: "#f5f5f5",
                          }}
                        />
                      </Box>

                      {/* Action buttons for selected slot */}
                      {(() => {
                        const currentSlotKey = selectedSlide && selectedSlotIndex !== null ? `${selectedSlide.sequence_id}_${selectedSlotIndex}` : null;
                        const isThisSlotUploading = uploadingSlotKeys.has(currentSlotKey);
                        const isThisSlotRegenerating = regeneratingSlotKeys.has(currentSlotKey);
                        const isThisSlotProcessing = isThisSlotUploading || isThisSlotRegenerating;

                        return (
                          <Box sx={{ display: "flex", gap: 1, marginBottom: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={isThisSlotUploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
                              onClick={() => fileInputRef.current?.click()}
                              disabled={isThisSlotProcessing}
                              sx={{
                                flex: 1,
                                borderColor: PRIMARY_BLUE,
                                color: PRIMARY_BLUE,
                                fontSize: "12px",
                                "&:hover": { borderColor: "#1748b8", backgroundColor: "#f5f7ff" },
                              }}
                            >
                              {isThisSlotUploading ? "Uploading..." : "Replace"}
                            </Button>

                            {getSlotByIndex(selectedSlide, selectedSlotIndex)?.imageType === "Ai" && (
                              <Tooltip title="Credits required: 0.1" arrow sx={{ flex: 1 }}>
                                <span style={{ flex: 1, display: "flex" }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={isThisSlotRegenerating ? <CircularProgress size={14} /> : <AutorenewIcon />}
                                    onClick={() => handleImageRegenerate(selectedSlotIndex)}
                                    disabled={isThisSlotProcessing}
                                    sx={{
                                      flex: 1,
                                      borderColor: PRIMARY_BLUE,
                                      color: PRIMARY_BLUE,
                                      fontSize: "12px",
                                      "&:hover": { borderColor: "#1748b8", backgroundColor: "#f5f7ff" },
                                    }}
                                  >
                                    {isThisSlotRegenerating ? "Generating..." : "Regenerate"}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  )}


                </Box>
              )}
            </Box>
          )}
        </Box>
      </SidebarTopbar>

      {/* PPT View Dialog */}
      <PresentationViewDialog
        open={pptDialogOpen}
        onClose={() => setPptDialogOpen(false)}
        pptUrl={pptUrl}
        pptId={pptId}
        professionalType={professionalType}
        onDownload={() => pptUrl && window.open(pptUrl, "_blank")}
        onEdit={() => {
          setPptDialogOpen(false);
          // Already in SlideEditorView, just refresh the slides
          fetchSlides();
        }}
      />

      {/* Alert Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ top: "80px !important" }}
      >
        <Alert onClose={handleAlertClose} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

SlideEditorView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  pptId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  token: PropTypes.string,
  publicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPptCreated: PropTypes.func,
  professionalType: PropTypes.string,
};

SlideEditorView.defaultProps = {
  pptId: null,
  token: null,
  publicationId: null,
  onPptCreated: null,
  professionalType: null,
};

export default SlideEditorView;